import { Group, Pouch, Sheet } from '../types';
import {
  getSortedLayers,
  layerDisplayName,
  mirrorPouchesForLayer,
  sheetHasMultipleLayers,
  calculateSheetTotalForLayer,
} from '../logic/LayerLogic';
import { getPouchContributionForTotals } from '../logic/BalancingPouchLogic';
import { applyPouchScaleAndRound, getPouchScaledValue } from './pouchScaleAndRound';
import { calculateColorTotal, colorLegendShowsTimeConversion } from '../logic/LegendsLogic';
import { calculateStatistics } from '../logic/StatisticsLogic';
import { isInMuteGroup, groupShowsTimeConversion, pouchCountsAsTimeForSheetDisplay, sheetContributingPouchesAreAllTime } from '../logic/GroupLogic';
import { formatPouchNumericTooltipBoldBlock } from './tooltipFormatter';

export { sheetHasMultipleLayers };

const INT_SNAP_EPS_SUBTOTAL = 1e-4;

/** グループ小計・合計のプレーン表示（時間レイヤーなら (h:mm) 付き） */
export function formatGroupSubtotalPlainVisible(
  contribution: number,
  showTimeConversion: boolean
): string {
  const rounded10 = Math.round(contribution * 1e10) / 1e10;
  const v =
    Math.abs(rounded10 - Math.round(rounded10)) <= INT_SNAP_EPS_SUBTOTAL
      ? Math.round(rounded10)
      : rounded10;
  const rounded8 = Math.round(v * 1e8) / 1e8;
  const intPart = Math.floor(Math.abs(rounded8));
  const decPart = Math.abs(rounded8) - intPart;
  let numeric =
    decPart > 0
      ? `${rounded8 < 0 ? '-' : ''}${intPart.toLocaleString()}.${decPart.toFixed(8).replace(/0+$/, '').substring(2)}`
      : `${rounded8 < 0 ? '-' : ''}${intPart.toLocaleString()}`;
  if (!showTimeConversion) return numeric;
  const totalMinutes = Math.round(Math.abs(v));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const timeSign = totalMinutes === 0 ? '' : v < 0 ? '-' : '';
  return `${numeric} (${timeSign}${hours}:${minutes.toString().padStart(2, '0')})`;
}

function normalizeTooltipText(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

function lineEquationPart(line: string): string {
  const idx = line.indexOf(': ');
  return idx >= 0 ? line.slice(idx + 2).trim() : line.trim();
}

/** getPouchTooltipTitle の [lastExpression] 行（比較用。ツールチップ本文には載せない） */
function stripExpressionAnnotation(text: string): string {
  return text.replace(/\s*\[[^\]]+\]\s*$/m, '').trim();
}

export type LayerNumericTooltipOptions = {
  /**
   * ×1 以外: 丸は左辺のみ表示のため、式が layer 行と一致しても数式詳細を出す。
   * パウチの乗数が 1 でないときに true。
   */
  forceEquationTooltip?: boolean;
};

/** 数式ツールチップを出すか（診断・テスト用） */
export function explainLayerNumericTooltipDecision(
  sheet: Sheet | undefined,
  visiblePlain: string,
  layerLinesPlain: string[],
  options?: LayerNumericTooltipOptions
): {
  shown: boolean;
  reason: string;
  visibleNormalized: string;
  layerEquationNormalized: string | null;
  multiLayerSheet: boolean;
} {
  if (!layerLinesPlain.length) {
    return {
      shown: false,
      reason: 'layerLines が空',
      visibleNormalized: normalizeTooltipText(visiblePlain),
      layerEquationNormalized: null,
      multiLayerSheet: sheetHasMultipleLayers(sheet),
    };
  }
  const visible = normalizeTooltipText(stripExpressionAnnotation(visiblePlain));
  const multiLayerSheet = sheetHasMultipleLayers(sheet);
  const layerEquationNormalized =
    layerLinesPlain.length === 1
      ? normalizeTooltipText(lineEquationPart(layerLinesPlain[0]))
      : null;
  if (!options?.forceEquationTooltip && (!sheet || !multiLayerSheet)) {
    if (
      layerLinesPlain.length === 1 &&
      layerEquationNormalized === visible
    ) {
      return {
        shown: false,
        reason: '単層×1: 表示と layer 行が同一（冗長のため非表示）',
        visibleNormalized: visible,
        layerEquationNormalized,
        multiLayerSheet,
      };
    }
  }
  if (options?.forceEquationTooltip) {
    const body = layerLinesPlain.map(normalizeTooltipText).join('\n');
    if (!body) {
      return {
        shown: false,
        reason: 'body が空',
        visibleNormalized: visible,
        layerEquationNormalized,
        multiLayerSheet,
      };
    }
    return {
      shown: true,
      reason: '×1 以外（左辺のみ表示のため詳細を表示）',
      visibleNormalized: visible,
      layerEquationNormalized,
      multiLayerSheet,
    };
  }
  const body = layerLinesPlain.map(normalizeTooltipText).join('\n');
  if (!body) {
    return {
      shown: false,
      reason: 'body が空',
      visibleNormalized: visible,
      layerEquationNormalized,
      multiLayerSheet,
    };
  }
  if (multiLayerSheet) {
    return {
      shown: true,
      reason: '多層シート（レイヤー別内訳を表示）',
      visibleNormalized: visible,
      layerEquationNormalized,
      multiLayerSheet,
    };
  }
  if (layerLinesPlain.length > 1) {
    return {
      shown: true,
      reason: '複数 layer 行',
      visibleNormalized: visible,
      layerEquationNormalized,
      multiLayerSheet,
    };
  }
  return {
    shown: true,
    reason: '単層: 表示と layer 行が不一致',
    visibleNormalized: visible,
    layerEquationNormalized,
    multiLayerSheet,
  };
}

/** 単層×1 で表示と同一なら undefined。×1 以外は options.forceEquationTooltip で常に表示 */
export function resolveLayerNumericTooltipHtml(
  sheet: Sheet | undefined,
  visiblePlain: string,
  layerLinesPlain: string[],
  options?: LayerNumericTooltipOptions
): string | undefined {
  const decision = explainLayerNumericTooltipDecision(
    sheet,
    visiblePlain,
    layerLinesPlain,
    options
  );
  if (!decision.shown) return undefined;
  const body = layerLinesPlain.map(normalizeTooltipText).join('\n');
  if (!body) return undefined;
  return formatPouchNumericTooltipBoldBlock(body);
}

export function appendGuideToTooltip(
  numericHtml: string | undefined,
  guideHtml: string | undefined
): string | undefined {
  const guide = guideHtml?.trim();
  if (!numericHtml) return guide || undefined;
  if (!guide) return numericHtml;
  return `${numericHtml}<br/>${guide}`;
}

function formatPlainNumber(value: number): string {
  const r8 = Math.round(value * 1e8) / 1e8;
  const r9 = Math.round(value * 1e9) / 1e9;
  const hasMore = Math.abs(r9 - r8) > 0;
  const intPart = Math.floor(Math.abs(r8));
  const decPart = Math.abs(r8) - intPart;
  const sign = r8 < 0 ? '-' : '';
  let result =
    decPart > 0
      ? `${sign}${intPart.toLocaleString()}.${decPart.toFixed(8).replace(/0+$/, '').substring(2)}`
      : `${sign}${intPart.toLocaleString()}`;
  if (hasMore) result += '...';
  return result;
}

function appendTimeConversionSuffix(numericResult: number, resultDisplay: string): string {
  const totalMinutes = Math.round(Math.abs(numericResult));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${resultDisplay} (${hours}:${minutes.toString().padStart(2, '0')})`;
}

function multiplierParts(multiplier: number, isTime?: boolean): { op: string; val: string } {
  const abs = Math.abs(multiplier);
  if (multiplier < 0) {
    const v = abs >= 1 || abs === Math.floor(abs) ? abs : abs;
    return { op: '÷', val: formatPlainNumber(v) };
  }
  if (abs >= 1 || abs === Math.floor(abs)) {
    return { op: '×', val: formatPlainNumber(abs) };
  }
  // 時間パウチ: 1未満も 0.75× 表示（% にしない）
  if (isTime) {
    return { op: '×', val: formatPlainNumber(Math.round(abs * 100) / 100) };
  }
  return { op: '×', val: `${formatPlainNumber(abs * 100)}%` };
}

/** パウチ1件の「値 × 乗数 ~N = 結果」行（プレーンテキスト） */
export function formatPouchEquationPlain(
  pouch: Pouch,
  valueOverride?: number,
  showTimeConversion?: boolean
): string {
  const isTime = showTimeConversion ?? pouch.isTime === true;
  const currentValue = valueOverride ?? pouch.value;
  const mult = pouch.multiplier;
  const effective = applyPouchScaleAndRound(
    { ...pouch, isTime },
    valueOverride,
  );
  const rounded = Math.round(effective * 1e10) / 1e10;
  const { op, val: multStr } = multiplierParts(mult, isTime);
  let mid = `${formatPlainNumber(currentValue)} ${op} ${multStr}`;
  if (pouch.roundUnit !== undefined && pouch.roundUnit > 0) {
    mid += ` ~ ${formatPlainNumber(pouch.roundUnit)}`;
  }
  let resultDisplay = formatPlainNumber(rounded);
  if (isTime) {
    resultDisplay = appendTimeConversionSuffix(rounded, resultDisplay);
  }
  return `${mid} = ${resultDisplay}`;
}

/** 数式詳細を強制表示すべきか（×1 でも ~ 丸めで実効値が scale のみと異なる） */
export function shouldForcePouchEquationTooltip(pouch: Pick<Pouch, 'multiplier' | 'roundUnit' | 'isTime' | 'value'>): boolean {
  const pureMult = pouch.multiplier < 0 ? 1 / Math.abs(pouch.multiplier) : Math.abs(pouch.multiplier);
  if (pureMult !== 1) {
    return true;
  }
  const ru = pouch.roundUnit;
  if (ru === undefined || ru <= 0) {
    return false;
  }
  const scaled = getPouchScaledValue(pouch);
  const effective = applyPouchScaleAndRound(pouch);
  return Math.abs(scaled - effective) > 1e-9;
}

export function buildPouchLayerTooltipLines(
  sheet: Sheet,
  pouch: Pouch,
  allPouches: Pouch[],
  allGroups: Group[],
): string[] {
  return getSortedLayers(sheet).map(layer => {
    const mirrored = mirrorPouchesForLayer(allPouches, layer.id, sheet, allGroups);
    const mp = mirrored.find(p => p.id === pouch.id);
    if (!mp) return `${layerDisplayName(layer)}: —`;
    const showTime = pouchCountsAsTimeForSheetDisplay(
      mp,
      mirrored,
      allGroups,
      sheet.balancingPouchId
    );
    return `${layerDisplayName(layer)}: ${formatPouchEquationPlain(mp, undefined, showTime)}`;
  });
}

export function buildGroupSubtotalLayerTooltipLines(
  sheet: Sheet,
  group: Group,
  allPouches: Pouch[],
  allGroups: Group[],
  /** レイヤーごとの mirror を先に作っておくとグループ数ぶんの重複を避けられる */
  mirroredByLayer?: ReadonlyMap<string, Pouch[]>
): string[] {
  return getSortedLayers(sheet).map(layer => {
    const mirrored =
      mirroredByLayer?.get(layer.id) ??
      mirrorPouchesForLayer(allPouches, layer.id, sheet, allGroups);
    const groupPouches = mirrored.filter(
      p =>
        group.pouches?.includes(p.id) &&
        !p._isDeleting &&
        !p._isGtpRenderPreview &&
        !allGroups.some(g => g.color === 'mute' && g.pouches?.includes(p.id))
    );
    const total = groupPouches.reduce(
      (sum, p) => sum + getPouchContributionForTotals(p, allGroups, mirrored, group.id),
      0
    );
    const showTime = groupShowsTimeConversion(
      group,
      mirrored,
      allGroups,
      sheet.balancingPouchId
    );
    return `${layerDisplayName(layer)}: ${formatGroupSubtotalPlainVisible(total, showTime)}`;
  });
}

export function buildColorTotalLayerTooltipLines(
  sheet: Sheet,
  colorId: string,
  allPouches: Pouch[],
  allGroups: Group[]
): string[] {
  return getSortedLayers(sheet).map(layer => {
    const mirrored = mirrorPouchesForLayer(allPouches, layer.id, sheet, allGroups);
    const total = calculateColorTotal(mirrored, colorId, allGroups);
    const showTime = colorLegendShowsTimeConversion(
      colorId,
      mirrored,
      allGroups,
      sheet.balancingPouchId
    );
    return `${layerDisplayName(layer)}: ${formatGroupSubtotalPlainVisible(total, showTime)}`;
  });
}

export function buildSheetTotalLayerTooltipLines(
  sheet: Sheet,
  allPouches: Pouch[],
  allGroups: Group[]
): string[] {
  return getSortedLayers(sheet).map(layer => {
    const mirrored = mirrorPouchesForLayer(allPouches, layer.id, sheet, allGroups);
    const total = calculateSheetTotalForLayer(allPouches, allGroups, layer.id, sheet);
    const showTime = sheetContributingPouchesAreAllTime(
      mirrored,
      allGroups,
      sheet.balancingPouchId
    );
    return `${layerDisplayName(layer)}: ${formatGroupSubtotalPlainVisible(total, showTime)}`;
  });
}

export function buildStatsTotalLayerTooltipLines(
  sheet: Sheet,
  allPouches: Pouch[],
  allGroups: Group[]
): string[] {
  return getSortedLayers(sheet).map(layer => {
    const mirrored = mirrorPouchesForLayer(allPouches, layer.id, sheet, allGroups);
    const stats = calculateStatistics(mirrored, allGroups, sheet.balancingPouchId);
    return `${layerDisplayName(layer)}: ${formatPlainNumber(stats.total)}`;
  });
}

export function computeTotalForSidebar(
  sheet: Sheet,
  pouches: Pouch[],
  groups: Group[],
  layerId?: string
): number {
  if (sheet.layers?.length && layerId) {
    return calculateSheetTotalForLayer(pouches, groups, layerId, sheet);
  }
  return pouches
    .filter(p => !p._isDeleting && !p._isGtpRenderPreview && !isInMuteGroup(p.id, groups))
    .reduce((t, p) => t + getPouchContributionForTotals(p, groups, pouches), 0);
}

export function computeSheetTotalForSidebar(
  sheet: Sheet,
  layerId?: string
): number {
  return computeTotalForSidebar(sheet, sheet.pouches ?? [], sheet.groups ?? [], layerId);
}
